import axios from 'axios';
import moment from 'moment';

import memoize = require('lodash/memoize');
import groupBy = require('lodash/groupBy');
import chain = require('lodash/chain');

import { IEvent, IOption, IMonth, IDay, IProduction } from '../interfaces';
import { ICalendarFilterRequest } from '../classes/calendar-request';
import { getDateLocalTime } from './time-zone-helper';
const weekdays = moment.weekdays().map(dayName => dayName.toLowerCase());

export class CalendarApi {
    private static apiDateFormat = "YYYY-MM-DD";
    private static aceCalendarAPIRoute = "ace-api/events";
    private static filtersSource = axios.CancelToken.source();

    /**
     * Private method to construct and process Filters API calls
     * @param filter 
     * @param startDate 
     * @param endDate 
     * @param culture 
     */
    private static getFiltersApi(filter: string, startDate: string, endDate: string, culture: string): Promise<IOption[]> {
        var requestString: string = '/';

        if (culture.length > 0)
            requestString += culture + '/';
        requestString = `${requestString}${this.aceCalendarAPIRoute}/filter?name=${filter}&start=${startDate}&end=${endDate}`;

        return axios.get<IOption[]>(requestString, {
            withCredentials: true
        }).then(response => response.data);
    }

    /**
     * Call the API to receive a more specific set of filters.
     * @param filter
     * @param culture
     */
    public static getFiltersFor(filter: string, startDate: moment.Moment, endDate: moment.Moment, culture = ""): Promise<IOption[]> {
        const startDateFormatted = startDate.format(this.apiDateFormat);
        const endDateFormatted = endDate.format(this.apiDateFormat);

        return this.getFiltersApi(filter, startDateFormatted, endDateFormatted, culture);
    }

    private static getFirstOccurenceForFilterApi(currentFilters: Map<string, string>, endDate: string, culture: string): Promise<string> {
        const startDate = moment().startOf("month").format(this.apiDateFormat);

        var requestString: string = '/';

        if (culture.length > 0) {
            requestString += culture + '/';
        }

        requestString = `${requestString}${this.aceCalendarAPIRoute}/firstOccurrence`;

        //var calendarFilters = [];
        //(Array.from(currentFilters)).forEach((val, key) => {
        //    calendarFilters.push({ filterName: key, filterValue: val });
        //});

        const calendarFilters = (Object as any).entries(currentFilters)
            .map(arr => { return { filterName: arr[0], filterValue: arr[1] } })

        const filterRequest: ICalendarFilterRequest = {
            startDate: startDate,
            endDate: endDate,
            calendarFilters: calendarFilters

        }
        return axios.post(requestString,
            filterRequest,
            {
                method: "POST",
                withCredentials: true,
                cancelToken: this.filtersSource.token
            }
        ).then(response => response.data);
    }

    /**
    * Call the API to receive a more specific set of filters.
    * @param filter
    * @param culture
    */
    public static getFirstOccurenceForFilters(filters: Map<string, string>, endDate: moment.Moment, culture = ""): Promise<string> {
        const endDateFormatted = endDate.format(this.apiDateFormat);

        return this.getFirstOccurenceForFilterApi(filters, endDateFormatted, culture);

        // This is the memoized variant. Since the request is cancelable, memoize would need to be tweaked to be properly utilized.
        //return this.filtersCache(filter, culture);
    }

    public static getMonth(month: string, filterFunction: (event) => boolean): Promise<IMonth<IEvent>> {

        const monthMoment = moment(month).startOf('month');

        return this.getMonthEventsCache(monthMoment.format(this.apiDateFormat)).then(monthEvents => {

            // convert response into IMonth<IEvent> object
            //const filteredEvents = monthEvents.filter(filterFunction);
            return {
                monthMoment,
                events: {
                    days: this.getDatesFromApiResponse(monthEvents, monthMoment),
                    productions: this.getProductionsFromApiResponse(monthEvents, monthMoment),
                    allDays: []
                }
            };
        })
    }

    public static getMonthEventsCache = memoize(CalendarApi.getMonthEvents,
        (month: string) => `${month}`);

    /**
     * Load events for a specific month.
     * @param month
     */
    private static getMonthEvents(month: string): Promise<Array<any>> {
        var requestString: string = '/';

        const monthMoment = moment(month).startOf('month');
        const endDate = monthMoment.clone().add(1, 'month');

        requestString = requestString + this.aceCalendarAPIRoute + "?startDate=" + monthMoment.format(this.apiDateFormat) + "&endDate=" + endDate.format(this.apiDateFormat);

        // GET events
        return axios.get<IEvent[]>(requestString, {
            withCredentials: true
        }).then(response => {

            return response.data;
        });
    }

    private static getDatesFromApiResponse(apiEvents: IEvent[], monthMoment: moment.Moment): IDay<IEvent>[] {
        // clean data
        apiEvents.forEach(event => {
            event.eventDate = new Date(event.eventDate);
        });
        const now = moment();
        // filter and sort
        apiEvents = apiEvents.filter(evt =>
            moment(evt.eventDate as Date).isSameOrAfter(monthMoment)
            && moment(evt.eventDate as Date).isSameOrAfter(now));
        apiEvents = apiEvents.sort((e1, e2) => (e1.eventDate as any) - (e2.eventDate as any)) as IEvent[];

        // format dates
        const datesInMonth = (() => {
           

         
            const dates: number[] = [];
            const daysInMonth = monthMoment.daysInMonth();
            for (let i = 1; i <= daysInMonth; i++) {
                dates.push(i);
            }
            return dates;
        })();

        // compile into array of IDay<IEvent> objects
        const days: IDay<IEvent>[] = datesInMonth.map(day => {
            const date = monthMoment.toDate();
            date.setDate(day);
            const events = apiEvents.filter(event => getDateLocalTime(event.eventDate) === day);
            return {
                date,
                events
            };
        });
        const datesBefore: number[] = [];
        const firstIndex = weekdays.indexOf(monthMoment.format('dddd').toLowerCase());
        let day = monthMoment.clone().add(-firstIndex, 'days');
        for (let i = 0; i < firstIndex; i++) {
            const date = day.clone().add(i, 'd');
            datesBefore.push(date.date());
        }
        const daysFromLastMonthInSameWeek: IDay<IEvent>[] = datesBefore.map(day => {
            const date = monthMoment.clone().add(-1,'M').toDate();
            date.setDate(day);
            const events = [];
            return {
                date,
                events
            };
        });
        let result = daysFromLastMonthInSameWeek.concat(days);
    
        return result;
    }

    private static getProductionsFromApiResponse(apiEvents: IEvent[], monthMoment: moment.Moment): IProduction<IEvent>[] {
        // clean data
        apiEvents.forEach(event => {
            event.eventDate = new Date(event.eventDate);
        });
        const now = moment();
        // filter and sort
        apiEvents = apiEvents.filter(evt =>
            moment(evt.eventDate as Date).isSameOrAfter(monthMoment)
            && moment(evt.eventDate as Date).isSameOrAfter(now));
        apiEvents = apiEvents.sort((e1, e2) => (e1.eventDate as any) - (e2.eventDate as any)) as IEvent[];

        var productionGrouped = groupBy(apiEvents, (evt) => {
            return evt.productionSeasonId;
        });

        // compile into array of IProduction<IEvent> objects
        let productions: IProduction<IEvent>[] = [];
        for (var key in productionGrouped) {
            if (productionGrouped.hasOwnProperty(key)) {
                var prodEvents = productionGrouped[key];
                var production: IProduction<IEvent> = {
                    ...prodEvents[0],
                    id: prodEvents[0].productionSeasonId.toString(),
                    conductor: prodEvents[0].composerInfo,
                    priceInfo: prodEvents[0].priceInfo,
                    firstPerformanceDate: prodEvents[0].productionStartDateString,
                    lastPerformanceDate: prodEvents[0].productionEndDateString,
                    events: prodEvents,
                    eventType: prodEvents[0].eventType
                };

                productions.push(
                    production
                );
            }
        }

        return productions;
    }
}